import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, RowsSkeletonLoader } from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import { SidePanelSection } from 'components/SidePanel/SidePanelSection'
import { SidePanelSummary } from 'components/SidePanel/SidePanelSummary'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import {
  Application,
  CustomerApplicationDetails,
  type IndividualApplicationDetails,
} from 'types/customers'
import { formatEnumValue, statusLabelMap } from '../utils/utils'
import { CustomerOnboardingTimeline } from './CustomerOnboardingTimeline'

interface ApplicationSidePanelBodyProps {
  application: Application | undefined
  data: CustomerApplicationDetails | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
  isReliance?: boolean
  hasUploadedDocuments?: boolean
}

interface ApplicationDetailsSectionProps {
  reference: string | undefined
  id: string | undefined
  type: string | undefined
  model: string | undefined
  status: Application['status'] | undefined
}

const IndividualDetailsSection = ({ individual }: { individual: IndividualApplicationDetails }) => {
  const { t } = useTranslation()
  return (
    <SidePanelSection heading={t('customers.individualDetails')}>
      <SidePanelSummary
        rows={[
          ...(individual.firstName
            ? [{ label: t('customers.firstName'), content: individual.firstName }]
            : []),
          ...(individual.lastName
            ? [{ label: t('customers.lastName'), content: individual.lastName }]
            : []),
          ...(individual.dateOfBirth
            ? [{ label: t('customers.dateOfBirth'), content: individual.dateOfBirth }]
            : []),
          ...(individual.emailAddress
            ? [{ label: t('customers.emailAddress'), content: individual.emailAddress }]
            : []),
          ...(individual.phoneNumber
            ? [{ label: t('customers.phoneNumber'), content: individual.phoneNumber }]
            : []),
          ...(individual.nationality
            ? [{ label: t('customers.nationality'), content: individual.nationality }]
            : []),
          ...(individual.placeOfBirth
            ? [{ label: t('customers.placeOfBirth'), content: individual.placeOfBirth }]
            : []),
          ...(individual.taxIdentification?.number
            ? [{ label: t('customers.taxNumber'), content: individual.taxIdentification.number }]
            : []),
          ...(individual.taxIdentification?.taxResidenceCountryCode
            ? [
                {
                  label: t('customers.taxResidency'),
                  content: individual.taxIdentification.taxResidenceCountryCode,
                },
              ]
            : []),
        ]}
      />
    </SidePanelSection>
  )
}

const IndividualAddressSection = ({ individual }: { individual: IndividualApplicationDetails }) => {
  const { t } = useTranslation()
  const { address } = individual
  if (!address) return null
  return (
    <SidePanelSection heading={t('customers.residentialAddress')}>
      <SidePanelSummary
        rows={[
          ...(address.addressLine1
            ? [{ label: t('customers.addressLine1'), content: address.addressLine1 }]
            : []),
          ...(address.addressLine2
            ? [{ label: t('customers.addressLine2'), content: address.addressLine2 }]
            : []),
          ...(address.city ? [{ label: t('customers.city'), content: address.city }] : []),
          ...(address.postalCode
            ? [{ label: t('customers.postalCode'), content: address.postalCode }]
            : []),
          ...(address.stateCode
            ? [{ label: t('customers.stateCode'), content: address.stateCode }]
            : []),
          ...(address.countryCode
            ? [{ label: t('customers.countryCode'), content: address.countryCode }]
            : []),
        ]}
      />
    </SidePanelSection>
  )
}

const IndividualCddSection = ({ individual }: { individual: IndividualApplicationDetails }) => {
  const { t } = useTranslation()
  const { cdd } = individual
  if (!cdd) return null
  return (
    <SidePanelSection heading={t('customers.cddDetails')}>
      <SidePanelSummary
        rows={[
          ...(cdd.employmentStatus
            ? [
                {
                  label: t('customers.employmentStatus'),
                  content: formatEnumValue(cdd.employmentStatus),
                },
              ]
            : []),
          ...(cdd.sourceOfFunds
            ? [{ label: t('customers.sourceOfFunds'), content: formatEnumValue(cdd.sourceOfFunds) }]
            : []),
          ...(cdd.pepStatus
            ? [{ label: t('customers.pepStatus'), content: formatEnumValue(cdd.pepStatus) }]
            : []),
          ...(cdd.intendedUseOfAccount
            ? [
                {
                  label: t('customers.intendedUse'),
                  content: formatEnumValue(cdd.intendedUseOfAccount),
                },
              ]
            : []),
          ...(cdd.expectedMonthlyVolume
            ? [
                {
                  label: t('customers.expectedMonthlyVolume'),
                  content: formatEnumValue(cdd.expectedMonthlyVolume),
                },
              ]
            : []),
        ]}
      />
    </SidePanelSection>
  )
}

const ApplicationDetailsSection = ({
  reference,
  id,
  type,
  model,
  status,
}: ApplicationDetailsSectionProps) => {
  const { t } = useTranslation()
  return (
    <SidePanelSection heading={t('customers.applicationDetails')}>
      <SidePanelSummary
        rows={[
          {
            label: t('customers.externalReference'),
            content: ellipseMiddleText(reference || id || '', 12, 8),
            ...((reference || id) && { copyText: reference || id }),
          },
          { label: t('customers.customerType'), content: type || '' },
          { label: t('customers.onboardingType'), content: model || '' },
          { label: t('status'), content: (status && statusLabelMap[status]) || status || '' },
        ]}
      />
    </SidePanelSection>
  )
}

export const CustomerSidePanelBody = ({
  application,
  data,
  isLoading,
  isError,
  refetch,
  isReliance,
  hasUploadedDocuments,
}: ApplicationSidePanelBodyProps) => {
  if (isLoading) {
    return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  }

  if (isError) {
    return (
      <Box paddingX={16} paddingY={32}>
        <RequestErrorStatusCard onReload={refetch} />
      </Box>
    )
  }

  const { reference, id, status, type, model } = application || {}
  const isV1Customer = !application?.authenticatedLink?.link

  const individual = data?.individual

  return (
    <Box flexCol gapY={16} padding={16}>
      <CustomerOnboardingTimeline
        application={application}
        data={data}
        isV1Customer={isV1Customer}
        isReliance={isReliance}
        hasUploadedDocuments={hasUploadedDocuments}
      />
      <ApplicationDetailsSection
        reference={reference}
        id={id}
        type={type}
        model={model}
        status={status}
      />
      {individual && <IndividualDetailsSection individual={individual} />}
      {individual && <IndividualAddressSection individual={individual} />}
      {individual && <IndividualCddSection individual={individual} />}
    </Box>
  )
}
